/*
 * DocumentCounterForm.tsx : Un formulaire pour ajouter ou modifier un compteur de documents.
 */

// Import React
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Import FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStopwatch20 } from '@fortawesome/free-solid-svg-icons';

// Import Communs
import {
	finishFormSave,
	FormSaveActions,
	InputField,
	useFormDirty,
} from '../../../common';

// Import Service
import { DocumentCountersData, documentTypeNames } from '../types/types';
import { documentCountersApi } from '../services/documentCountersApi';

// Composant DocumentCounterForm
const DocumentCounterForm: React.FC = () => {
	// Hooks de State
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);
	const [formData, setFormData] = React.useState<DocumentCountersData>({
		id: 0,
		documentType: '',
		year: new Date().getFullYear(),
		lastNumber: 0,
	});

	// Hook de Navigation et Paramètres de navigation
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const { isDirty, markClean } = useFormDirty(formData, baselineReady);

	// Une fois le composant monté et si un id est présent, fetch du service et préremplissage du formulaire
	React.useEffect(() => {
		if (id) {
			const token = localStorage.getItem('token');
			if (!token) return;

			documentCountersApi
				.get(Number(id), token)
				.then((data) => setFormData(data))
				.catch((error) => console.error(error))
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			// Si pas d'id, on arrête le loading
			setLoading(false);
			setBaselineReady(true);
		}
	}, [id]);

	// Gestion des changements dans le formulaire
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	// Gestion de la soumission du formulaire
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const handleSave = (shouldLeave: boolean) => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		const onLeave = () => navigate('/app/settings/documents');
		// Si un id est présent, mise à jour du compteur
		if (id) {
			documentCountersApi
				.update(Number(id), formData, token)
				.then(() => finishFormSave(shouldLeave, { onLeave, markClean }))
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		} else {
			// Sinon, création d'un nouveau compteur
			documentCountersApi
				.create(formData, token)
				.then((created) =>
					finishFormSave(shouldLeave, {
						onLeave,
						markClean,
						stayOnEdit:
							!id && created?.id
								? () =>
										navigate(`/app/documentCounters/${created.id}/edit`, {
											replace: true,
										})
								: undefined,
					})
				)
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		// Empêcher le comportement par défaut du formulaire
		e.preventDefault();
		handleSave(true);
	};

	// Rendu du composant
	return (
		<div>
			{/* CARD PRINCIPALE */}
			<div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md">
				{/* HEADER DE LA CARD */}
				<div className="flex items-start mb-3 space-x-4">
					{/* Icône à gauche */}
					<FontAwesomeIcon
						icon={faStopwatch20}
						className="w-20 h-20 text-blue-900/90"
					/>

					{/* Titre + toggle à droite */}
					<div className="flex flex-col mt-3">
						<h1 className="text-3xl font-bold">
							{id ? "Édition d'un compteur chrono" : 'Nouveau compteur chrono'}
						</h1>
					</div>
				</div>

				{/* CONTENU DE LA CARD */}
				<div className="items-center space-x-4 mt-3 w-full">
					<form onSubmit={handleSubmit} className="space-y-4">
						{/* 2 COLONNES */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-16">
							{/* COLONNE GAUCHE */}
							<div className="space-y-4">
								<InputField
									label="Type"
									name="documentType"
									value={
										documentTypeNames[
											formData.documentType as keyof typeof documentTypeNames
										]
									}
									onChange={handleChange}
									required
									loading={loading}
									readOnly={true}
								/>
								<InputField
									label="Dernière mise à jour"
									name="updatedAt"
									value={
										formData.updatedAt
											? new Date(formData.updatedAt).toLocaleString('fr-FR', {
													year: 'numeric',
													month: '2-digit',
													day: '2-digit',
													hour: '2-digit',
													minute: '2-digit',
												})
											: undefined
									}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="ex : 124"
									readOnly={true}
								/>
							</div>

							{/* COLONNE DROITE */}
							<div className="space-y-4">
								<InputField
									label="Dernier numéro utilisé"
									name="lastNumber"
									value={formData.lastNumber}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="ex : 124"
								/>
								<InputField
									label="Année"
									name="year"
									value={formData.year}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="ex : 2026"
								/>
							</div>
						</div>
					</form>
					<FormSaveActions
						isDirty={isDirty}
						isSubmitting={isSubmitting}
						onCancel={() => navigate('/app/settings/documents')}
						onSave={() => handleSave(false)}
						onSaveAndLeave={() => handleSave(true)}
					/>
				</div>
			</div>
		</div>
	);
};

export default DocumentCounterForm;
